<script setup>
import { computed, ref, watch, nextTick } from 'vue';
import { useI18n } from 'vue-i18n';
import { INPUT_TYPES } from 'dashboard/components-next/taginput/helper/tagInputHelper.js';
import { formatBrazilianPhone } from 'dashboard/components-next/NewConversation/helpers/composeConversationHelper.js';
import ContactAPI from 'dashboard/api/contacts';
import BulkCampaignsAPI from 'dashboard/api/bulkCampaigns';

import TagInput from 'dashboard/components-next/taginput/TagInput.vue';
import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  contacts: {
    type: Array,
    required: true,
  },
  selectedContact: {
    type: Object,
    default: null,
  },
  showContactsDropdown: {
    type: Boolean,
    required: true,
  },
  isLoading: {
    type: Boolean,
    required: true,
  },
  isCreatingContact: {
    type: Boolean,
    required: true,
  },
  contactId: {
    type: String,
    default: null,
  },
  contactableInboxesList: {
    type: Array,
    default: () => [],
  },
  showInboxesDropdown: {
    type: Boolean,
    required: true,
  },
  hasErrors: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits([
  'searchContacts',
  'setSelectedContact',
  'clearSelectedContact',
  'updateDropdown',
]);

const i18nPrefix = 'COMPOSE_NEW_CONVERSATION.FORM.CONTACT_SELECTOR';
const { t } = useI18n();

const inputType = ref(INPUT_TYPES.EMAIL);

// Adição rápida de contato (nome + telefone) quando a busca não encontra ninguém
const searchText = ref('');
const showAddCard = ref(false);
const newNameInput = ref(null);
const newName = ref('');
const newPhone = ref('');
const addError = ref('');
const isSavingContact = ref(false);
const isCheckingContact = ref(false);
const similarContacts = ref([]);
const showSimilarConfirm = ref(false);

const showAddPrompt = computed(() => {
  return (
    props.showContactsDropdown &&
    searchText.value.trim().length >= 2 &&
    !props.isLoading &&
    (props.contacts || []).length === 0 &&
    !props.selectedContact &&
    !props.isCreatingContact
  );
});

// Sem resultados: esconde o balão vazio para não sobrepor o botão de adicionar
const showDropdown = computed(() => {
  if (
    searchText.value.trim().length >= 2 &&
    !props.isLoading &&
    (props.contacts || []).length === 0
  ) {
    return false;
  }
  return props.showContactsDropdown;
});

watch(
  () => props.selectedContact,
  contact => {
    if (contact) {
      showAddCard.value = false;
      showSimilarConfirm.value = false;
    }
  }
);

const openAddCard = () => {
  newName.value = '';
  newPhone.value = searchText.value.trim();
  addError.value = '';
  similarContacts.value = [];
  showSimilarConfirm.value = false;
  showAddCard.value = true;
  nextTick(() => newNameInput.value?.focus());
};

const cancelAddCard = () => {
  showAddCard.value = false;
  showSimilarConfirm.value = false;
  addError.value = '';
};

const findSimilarByName = async name => {
  try {
    const { data } = await ContactAPI.search(name.trim());
    const list = data?.payload || [];
    const digits = newPhone.value.replace(/\D/g, '');
    return list.filter(c => {
      const phone = String(c.phone_number || '').replace(/\D/g, '');
      return phone !== digits;
    });
  } catch (e) {
    return [];
  }
};

const checkWhatsAppNumber = async phone => {
  const { data } = await BulkCampaignsAPI.checkWhatsApp([
    { phone, validation_id: 0 },
  ]);
  const results = data?.results || [];
  return results[0] || {};
};

const persistNewContact = async () => {
  const { data } = await ContactAPI.create({
    name: newName.value.trim(),
    phone_number: formatBrazilianPhone(newPhone.value),
  });
  const created = data?.payload?.contact || data?.contact || {};
  return {
    value: created.id,
    action: 'contact',
    id: created.id,
    name: created.name,
    phoneNumber: created.phone_number,
    email: created.email || '',
    thumbnail: created.thumbnail || '',
  };
};

const saveNewContact = async (force = false) => {
  addError.value = '';
  if (!newName.value.trim()) {
    addError.value = t(`${i18nPrefix}.ADD_CONTACT.NAME_REQUIRED`);
    return;
  }
  if (!newPhone.value.trim()) {
    addError.value = t(`${i18nPrefix}.ADD_CONTACT.PHONE_REQUIRED`);
    return;
  }
  isSavingContact.value = true;
  try {
    if (!force) {
      isCheckingContact.value = true;
      const similar = await findSimilarByName(newName.value);
      isCheckingContact.value = false;
      if (similar.length > 0) {
        similarContacts.value = similar.slice(0, 3);
        showSimilarConfirm.value = true;
        isSavingContact.value = false;
        return;
      }
    }
    const check = await checkWhatsAppNumber(
      formatBrazilianPhone(newPhone.value)
    );
    if (check.error) {
      addError.value = t(`${i18nPrefix}.ADD_CONTACT.CHECK_ERROR`);
      isSavingContact.value = false;
      return;
    }
    if (check.exists !== true) {
      addError.value = t(`${i18nPrefix}.ADD_CONTACT.NO_WHATSAPP`);
      isSavingContact.value = false;
      return;
    }
    const contact = await persistNewContact();
    showAddCard.value = false;
    emit('setSelectedContact', contact);
  } catch (e) {
    addError.value = t(`${i18nPrefix}.ADD_CONTACT.CREATE_FAILED`);
  } finally {
    isSavingContact.value = false;
  }
};

const contactsList = computed(() => {
  return props.contacts?.map(({ name, id, thumbnail, email, ...rest }) => ({
    id,
    label: email ? `${name} (${email})` : name,
    value: id,
    thumbnail: { name, src: thumbnail },
    ...rest,
    name,
    email,
    action: 'contact',
  }));
});

const selectedContactLabel = computed(() => {
  const { name, email = '', phoneNumber = '' } = props.selectedContact || {};
  if (email) {
    return `${name} (${email})`;
  }
  if (phoneNumber) {
    return `${name} (${phoneNumber})`;
  }
  return name || '';
});

const errorClass = computed(() => {
  return props.hasErrors
    ? '[&_input]:placeholder:!text-n-ruby-9 [&_input]:dark:placeholder:!text-n-ruby-9'
    : '';
});

const handleInput = value => {
  searchText.value = value;
  // Update input type based on whether input starts with '+'
  // If it does, set input type to 'tel'
  // Otherwise, set input type to 'email'
  inputType.value = value.startsWith('+') ? INPUT_TYPES.TEL : INPUT_TYPES.EMAIL;
  emit('searchContacts', value);
};
</script>

<template>
  <div class="relative flex-1 px-4 py-3 overflow-y-visible">
    <div class="flex items-baseline w-full gap-3 min-h-7">
      <label class="text-sm font-medium text-n-slate-11 whitespace-nowrap">
        {{ t(`${i18nPrefix}.LABEL`) }}
      </label>

      <div
        v-if="isCreatingContact"
        class="flex items-center gap-1.5 rounded-md bg-n-alpha-2 px-3 min-h-7 min-w-0"
      >
        <span class="text-sm truncate text-n-slate-12">
          {{ t(`${i18nPrefix}.CONTACT_CREATING`) }}
        </span>
      </div>
      <div
        v-else-if="selectedContact"
        class="flex items-center gap-1.5 rounded-md bg-n-alpha-2 min-h-7 min-w-0"
        :class="!contactId ? 'ltr:pl-3 rtl:pr-3 ltr:pr-1 rtl:pl-1' : 'px-3'"
      >
        <span class="text-sm truncate text-n-slate-12">
          {{
            isCreatingContact
              ? t(`${i18nPrefix}.CONTACT_CREATING`)
              : selectedContactLabel
          }}
        </span>
        <Button
          v-if="!contactId"
          variant="ghost"
          icon="i-lucide-x"
          color="slate"
          :disabled="contactId"
          size="xs"
          @click="emit('clearSelectedContact')"
        />
      </div>
      <TagInput
        v-else
        :placeholder="t(`${i18nPrefix}.TAG_INPUT_PLACEHOLDER`)"
        mode="single"
        :menu-items="contactsList"
        :show-dropdown="showDropdown"
        :is-loading="isLoading"
        :disabled="contactableInboxesList?.length > 0 && showInboxesDropdown"
        allow-create
        :type="inputType"
        class="flex-1 min-h-7"
        :class="errorClass"
        focus-on-mount
        @input="handleInput"
        @on-click-outside="emit('updateDropdown', 'contacts', false)"
        @add="emit('setSelectedContact', $event)"
        @remove="emit('clearSelectedContact')"
      />
    </div>
    <div v-if="showAddPrompt && !showAddCard" class="mt-2">
      <div class="text-xs text-n-text-light dark:text-n-text-muted">
        {{ t(`${i18nPrefix}.ADD_CONTACT.NOT_FOUND`) }}
      </div>
      <Button
        variant="ghost"
        size="xs"
        color="slate"
        icon="i-lucide-plus"
        @click="openAddCard"
      >
        {{ t(`${i18nPrefix}.ADD_CONTACT.ADD_BUTTON`) }}
      </Button>
    </div>
    <form
      v-if="showAddCard"
      class="mt-2 p-3 rounded-xl border border-n-divider bg-white dark:bg-n-surface-2 space-y-2"
      @submit.prevent="saveNewContact(false)"
    >
      <div class="text-sm font-medium text-n-slate-12">
        {{ t(`${i18nPrefix}.ADD_CONTACT.TITLE`) }}
      </div>
      <div>
        <label class="block text-xs text-n-text-light dark:text-n-text-muted">
          {{ t(`${i18nPrefix}.ADD_CONTACT.NAME_LABEL`) }}
        </label>
        <input
          ref="newNameInput"
          v-model="newName"
          type="text"
          :placeholder="t(`${i18nPrefix}.ADD_CONTACT.NAME_PLACEHOLDER`)"
          class="w-full px-2 py-1.5 text-sm border border-n-divider rounded-lg bg-white dark:bg-n-surface-2 text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-primary-500"
        />
      </div>
      <div>
        <label class="block text-xs text-n-text-light dark:text-n-text-muted">
          {{ t(`${i18nPrefix}.ADD_CONTACT.PHONE_LABEL`) }}
        </label>
        <input
          v-model="newPhone"
          type="tel"
          :placeholder="t(`${i18nPrefix}.ADD_CONTACT.PHONE_PLACEHOLDER`)"
          class="w-full px-2 py-1.5 text-sm border border-n-divider rounded-lg bg-white dark:bg-n-surface-2 text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-primary-500"
        />
      </div>
      <div
        v-if="showSimilarConfirm"
        class="p-2 rounded-lg bg-n-amber-3 text-xs text-n-amber-11"
      >
        <div class="font-medium">
          {{ t(`${i18nPrefix}.ADD_CONTACT.SIMILAR_TITLE`) }}
        </div>
        <div v-for="c in similarContacts" :key="c.id || c.name">
          {{ c.name }}
        </div>
        <div class="flex gap-2 mt-2">
          <Button
            type="button"
            size="xs"
            color="slate"
            @click="saveNewContact(true)"
          >
            {{ t(`${i18nPrefix}.ADD_CONTACT.CREATE_ANYWAY`) }}
          </Button>
          <Button
            type="button"
            size="xs"
            variant="ghost"
            color="slate"
            @click="showSimilarConfirm = false"
          >
            {{ t(`${i18nPrefix}.ADD_CONTACT.BACK_TO_EDIT`) }}
          </Button>
        </div>
      </div>
      <div v-if="addError" class="text-xs text-red-600 dark:text-red-400">
        {{ addError }}
      </div>
      <div class="flex gap-2">
        <Button
          type="submit"
          size="xs"
          color="slate"
          :disabled="isSavingContact"
        >
          {{
            isSavingContact || isCheckingContact
              ? t(`${i18nPrefix}.ADD_CONTACT.SAVING`)
              : t(`${i18nPrefix}.ADD_CONTACT.SAVE`)
          }}
        </Button>
        <Button
          type="button"
          size="xs"
          variant="ghost"
          color="slate"
          :disabled="isSavingContact"
          @click="cancelAddCard"
        >
          {{ t(`${i18nPrefix}.ADD_CONTACT.CANCEL`) }}
        </Button>
      </div>
    </form>
  </div>
</template>
